import { useFormikContext } from 'formik';
import get from 'lodash/get';
import { type InputHTMLAttributes, useId } from 'react';

import FormErrorMessage from '@tmh-website/components/shared/FormErrorMessage';
import { Color } from '@tmh-website/shared/colors';
import type { FormElementCommonProps } from '@tmh-website/ui/FormElements';
import { StyledFormElements } from '@tmh-website/ui/FormElements';

import * as Styled from './Checkbox.styles';

export interface CheckboxProps
  extends Omit<FormElementCommonProps, 'label'>,
    Partial<Omit<InputHTMLAttributes<HTMLInputElement>, 'theme' | 'name'>> {
  /**
   * Determines whether the contents should be horizontally aligned.
   *
   * @default false
   */
  isCentered?: boolean;

  hideErrorMessage?: boolean;
  noLabelElement?: boolean;
}

const Checkbox = ({
  children,
  hideErrorMessage = false,
  isCentered = false,
  name,
  noLabelElement,
  required,
  themeColor = Color.Neutral_0_White,
  value,
  ...props
}: CheckboxProps) => {
  const uniqueId = useId();
  const nameWithId = name + '_' + uniqueId;
  const { values, errors, touched } = useFormikContext<any>();
  const hasError = get(errors, name) && get(touched, name);
  const isChecked =
    (Array.isArray(get(values, name)) ? get(values, name).includes(value) : get(values, name)) ??
    props.checked === true;

  return (
    <div>
      <Styled.Label
        as={noLabelElement ? 'div' : 'label'}
        $themeColor={themeColor}
        $isCentered={isCentered}
        $isChecked={isChecked}
        htmlFor={nameWithId}>
        <Styled.CheckboxInput
          $themeColor={themeColor}
          $isChecked={isChecked}
          $hasError={!!hasError}
          type='checkbox'
          name={name}
          id={nameWithId}
          checked={isChecked}
          value={value}
          required={required}
          {...props}
        />
        <Styled.ChildrenContainer $style='uiText'>{children}</Styled.ChildrenContainer>
      </Styled.Label>
      {hasError && !hideErrorMessage && (
        <StyledFormElements.ErrorMessage>
          <FormErrorMessage name={name} />
        </StyledFormElements.ErrorMessage>
      )}
    </div>
  );
};

export default Checkbox;
